import { FC, PropsWithChildren, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { CloudArrowUpFill, TrashFill } from 'react-bootstrap-icons';
import { Accept, DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import Color from 'color';

import { FileRestrictionsModel, FormImageModel } from '@/lib/models';
import { getFileDropzoneRejectionMessage } from '@/lib/utils/file-uploader-utils';
import { createUseThemedStyles } from '@/hooks';
import { Meter } from '@/components';

const useStyles = createUseThemedStyles((theme) => ({
	imageDropzone: {
		padding: '54px 0',
		...theme.fonts.xs,
		textAlign: 'center',
		color: theme.colors.gray600,
		backgroundColor: theme.colors.white,
		border: `1px dashed ${theme.colors.gray500}`,
		'& .fake-link': {
			color: theme.colors.primary,
			textDecoration: 'underline',
		},
		'&:hover': {
			cursor: 'pointer',
			borderColor: theme.colors.gray600,
			'& .fake-link': {
				color: Color(theme.colors.primary)
					.darken(0.16)
					.hex(),
			},
		},
	},
	fileChosen: {
		padding: 16,
		borderRadius: 4,
		display: 'flex',
		alignItems: 'center',
		backgroundColor: theme.colors.white,
		border: `1px solid ${theme.colors.gray300}`,
	},
	imagePreviewOuter: {
		maxWidth: 214,
		position: 'relative',
		'& img': {
			width: '100%',
		},
	},
	imageInformation: {
		flex: 1,
		padding: 25,
	},
	removeButton: {
		padding: '15px !important',
		display: 'flex',
		alignItems: 'center',
	},
	meterOuter: {
		left: 16,
		right: 16,
		bottom: 16,
		position: 'absolute',
	},
}));

interface ImageDropzoneProps {
	formImage?: FormImageModel;
	fileRestrictions: FileRestrictionsModel;
	onChange(formImage?: FormImageModel): void;
	percentage?: number;
	className?: string;
}

export const ImageDropzone = ({ formImage, fileRestrictions, onChange, percentage, className }: ImageDropzoneProps) => {
	const classes = useStyles();

	const handleImageFileDropzoneChange = useCallback(
		([file]: File[], fileRejections: FileRejection[]) => {
			if (fileRejections.length > 0) {
				window.alert(getFileDropzoneRejectionMessage(fileRejections));
				onChange(undefined);
				return;
			}

			if (!file) {
				onChange(undefined);
				return;
			}

			const image = new Image();
			const reader = new FileReader();

			reader.onload = (event) => {
				if (event.target?.result) {
					image.src = event.target.result as string;
				}
			};

			reader.readAsDataURL(file);

			image.onload = () => {
				const { width, height } = image;
				if (width < fileRestrictions.minImageWidth || height < fileRestrictions.minImageHeight) {
					window.alert(
						`Image minimum size is ${fileRestrictions.minImageWidth}x${fileRestrictions.minImageHeight}px.`
					);
					return;
				}

				onChange({
					imageFile: file,
					imageName: file.name,
					imageUrl: URL.createObjectURL(file),
				});
			};
		},
		[fileRestrictions.minImageHeight, fileRestrictions.minImageWidth, onChange]
	);

	return (
		<div className={className}>
			{formImage ? (
				<div className={classes.fileChosen}>
					<div className={classes.imagePreviewOuter}>
						<img src={formImage.imageUrl} alt={formImage.imageName} />
					</div>
					<div className={classes.imageInformation}>
						<span className="font-weight-semi-bold">{formImage.imageName}</span>
						{typeof percentage !== 'undefined' && (
							<div className={classes.meterOuter}>
								<Meter value={percentage} max={100} size="sm" pill showBackground />
							</div>
						)}
					</div>
					<div className="flex-shrink-0">
						<Button
							className={classes.removeButton}
							variant="danger"
							type="button"
							onClick={() => {
								onChange(undefined);
							}}
						>
							<TrashFill className="d-flex" />
						</Button>
					</div>
				</div>
			) : (
				<FileDropzone
					maxSize={10000000}
					accept={{
						'image/*': fileRestrictions.imageFileFormats.split(',').map((ext) => `.${ext}`.toLowerCase()),
					}}
					maxFiles={1}
					onChange={handleImageFileDropzoneChange}
				>
					<div className={classes.imageDropzone}>
						<span>
							<CloudArrowUpFill width={24} height={24} className="mr-2" />
							<span className="fake-link">Click to browse files</span> or drag &amp; drop
							<br />
							<span>
								{fileRestrictions.imageFileFormats
									.split(',')
									.map((ext) => `.${ext}`.toLowerCase())
									.join(', ')}{' '}
								(min. {fileRestrictions.minImageWidth}x{fileRestrictions.minImageHeight}px)
							</span>
						</span>
					</div>
				</FileDropzone>
			)}
		</div>
	);
};

interface FileDropzoneProps {
	accept?: Accept;
	maxSize?: number;
	maxFiles?: number;
	disabled?: boolean;
	onChange: <T extends File>(acceptedFiles: T[], fileRejections: FileRejection[], event: DropEvent) => void;
	dragActiveComponent?: JSX.Element;
}

const FileDropzone: FC<PropsWithChildren<FileDropzoneProps>> = ({
	accept,
	maxSize,
	maxFiles,
	disabled,
	onChange,
	dragActiveComponent,
	children,
}) => {
	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: onChange,
		accept,
		maxSize,
		maxFiles,
		disabled,
	});

	return (
		<div {...getRootProps()}>
			<input {...getInputProps()} />
			{isDragActive ? <>{dragActiveComponent ?? children} </> : <>{children}</>}
		</div>
	);
};
